// A made-up day for working on the grid without signing in: every
// status, every flag, a group, an overlap, a cancelled slot, a walk-in.
// Dev only — App.tsx shows it at /?demo under import.meta.env.DEV.
// The names are fictional.

import type {
  BoardBirthday, BoardCell, BoardClient, ClientBasics, ClientMoneyView, DayBoard, DeskClients, Me,
  SessionClientDetail, SessionDetail, SessionHistoryRow, WaRowView,
} from "../api/contract";

// A Wednesday, so a weekly series reads "Her Çarşamba" in the drawer.
const DATE = "2026-10-14";
const iso = (t: string) => `${DATE}T${t}:00+03:00`;
// The day n days before DATE (after it, for n < 0).
const daysBefore = (n: number): string =>
  new Date(Date.parse(`${DATE}T12:00:00Z`) - n * 864e5).toISOString().slice(0, 10);
// A birthday n days after DATE (before it, for n < 0).
const bday = (inDays: number, age: number): BoardBirthday =>
  ({date: daysBefore(-inDays), inDays, age});

const client = (id: string, name: string, o: Partial<BoardClient> = {}): BoardClient => ({
  clientId: id, name,
  sozlesme: {mark: "ok", text: null},
  deskNote: null,
  attendance: {state: "none", autoCancel: null, confirmMark: null, eligible: true},
  recorded: null,
  money: {collectNowTL: 0, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4000},
  ...o,
});

const cell = (
  id: string, providerId: string, time: string, endTime: string,
  clients: BoardClient[], status: BoardCell["status"],
  o: Partial<BoardCell> = {},
): BoardCell => ({
  id, providerId, start: iso(time), end: iso(endTime), time, endTime,
  durationMin: 50, scheduleId: `s-${id}`, recurringEventId: `m-${id}`,
  title: `${clients.map((c) => c.name).join(", ")} — ${providerId}`,
  isGroup: clients.length > 1, clients, status, cancellation: null, flags: [],
  ...o,
});

export const DEMO_BOARD: DayBoard = {
  date: DATE,
  generatedAt: `${DATE}T13:40:00.000Z`, // 16:40 at the desk
  hours: {from: "09:00", to: "21:00"},
  whatsapp: {enabled: true, pilot: true},
  providers: [
    {providerId: "p1", name: "Seda", calendarId: "c1", calendarOk: true, photo: true},
    {providerId: "p2", name: "Merve", calendarId: "c2", calendarOk: true, photo: true},
    {providerId: "p3", name: "Ceyda", calendarId: "c3", calendarOk: true, photo: true},
    {providerId: "p4", name: "Rayan", calendarId: "c4", calendarOk: false},
    {providerId: "p5", name: "Elif", calendarId: "c5", calendarOk: true},
  ],
  cells: [
    cell("a1", "p1", "09:00", "09:50", [client("k1", "Ada Yılmaz", {
      recorded: {serviceIds: ["s1"], covered: true, paidTL: 4000, uncoveredTL: 0, channels: ["cash"], linkedChannels: ["cash"]},
    })], "paid"),
    cell("a2", "p1", "10:00", "10:50", [client("k2", "Ege Kara", {
      recorded: {serviceIds: ["s2"], covered: false, paidTL: 0, uncoveredTL: 3500, channels: []},
      money: {collectNowTL: 3500, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4000},
      birthday: bday(6, 8),
    })], "recorded"),
    cell("a3", "p1", "11:00", "11:50", [
      client("k3", "Can Demir", {sozlesme: {mark: "resign", text: "sözleşme yenilenecek"},
        recorded: {serviceIds: ["s3"], covered: true}}),
      client("k4", "Deniz Ak", {attendance: {state: "confirmed", autoCancel: null, confirmMark: "marked", eligible: true}}),
    ], "planned", {flags: ["partially_recorded", "unrecorded_past"]}),
    cell("a4", "p1", "13:00", "13:50", [client("k5", "Mila Su", {
      attendance: {state: "confirmed", autoCancel: null, confirmMark: "marked", eligible: true,
        source: "phone"},
      money: {collectNowTL: 0, creditTL: 12000, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4000},
    })], "confirmed"),
    cell("a5", "p1", "15:00", "15:50", [client("k6", "Arda Çelik", {
      attendance: {state: "declined", autoCancel: "group_session", confirmMark: null,
        eligible: false},
      // Not coming on the day itself: the small cake, not the indigo.
      birthday: bday(0, 9),
    })], "declined"),
    cell("a6", "p1", "16:00", "16:50", [client("k7", "Lina Koç", {
      sozlesme: {mark: "missing", text: "sözleşme yok"},
      deskNote: "veli 10 dk geç gelecek",
      money: {collectNowTL: 9000, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4000},
      // Her birthday two days ago: the cell in the indigo, the cake drawn.
      birthday: bday(-2, 7),
    })], "planned"),
    // Merve's morning, paid in cash: what the Kasa drawer has her due.
    cell("b1", "p2", "09:30", "10:20", [client("k8", "Yaman Öz", {
      recorded: {serviceIds: ["s8"], covered: true, paidTL: 4000, uncoveredTL: 0,
        channels: ["cash"], linkedChannels: ["cash"]},
    })], "paid"),
    // An overlap: two cells share Merve's column for twenty minutes.
    cell("b2", "p2", "10:00", "10:50", [client("k9", "Nehir Taş", {
      recorded: {serviceIds: ["s9"], covered: true, paidTL: 4000, uncoveredTL: 0,
        channels: ["cash"], linkedChannels: ["cash"]},
    })], "paid"),
    cell("b3", "p2", "14:00", "14:50", [client("k10", "Kerem Aslan", {
      attendance: {state: "sent", autoCancel: null, confirmMark: null, eligible: true},
      recorded: {serviceIds: ["s10"], covered: true, paidTL: 4500, uncoveredTL: 0,
        channels: ["cash"], linkedChannels: ["cash"]},
      money: {collectNowTL: 0, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4500},
    })], "paid"),
    // Still on the calendar under a standing iptal, and a family that
    // answered "gelmiyor" on WhatsApp: the WhatsApp drawer's iptal and
    // gelmiyor rows.
    cell("b5", "p2", "12:00", "12:50", [client("k25", "Nil Yurt")], "cancelled",
      {cancellation: {cancellationId: "x3", kind: "cancelled"}, flags: ["still_on_calendar"]}),
    cell("b6", "p2", "15:00", "15:50", [client("k26", "Kaan Sezer", {
      attendance: {state: "declined", autoCancel: null, confirmMark: null, eligible: true},
    })], "declined"),
    // Some credit, less than the fee: the pay sheet takes the rest.
    cell("b4", "p2", "17:30", "18:20", [client("k11", "Zeynep Güneş", {
      money: {collectNowTL: 0, creditTL: 1000, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4000},
      birthday: bday(1, 4),
    })], "planned"),
    // Credit locked to this danışman pays for the seans.
    cell("c1", "p3", "12:00", "12:30", [client("k12", "Ali Kurt", {
      money: {collectNowTL: 0, creditTL: 0, restrictedCreditTL: 6000, providerCreditTL: 6000, feeTL: 3000},
    })], "planned"),
    // No fee for the pair: the pay sheet points to chat.
    cell("c2", "p3", "12:30", "13:20", [client("k13", "Ayşe Bal", {
      money: {collectNowTL: 0, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: null},
    })], "planned"),
    // Credit locked to Ceyda, and a seans with another danışman left
    // open: the locked credit pays this seans only, the rest is taken.
    cell("c4", "p3", "15:00", "15:50", [client("k23", "Selin Ay", {
      money: {collectNowTL: 2000, creditTL: 0, restrictedCreditTL: 6000, providerCreditTL: 6000, feeTL: 3000},
    })], "planned"),
    // Credit that covers the seans to the lira: nothing is left after.
    cell("c5", "p3", "17:00", "17:50", [client("k24", "Tuna Er", {
      money: {collectNowTL: 0, creditTL: 3000, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 3000},
    })], "planned"),
    cell("c3", "p3", "19:30", "20:20", [], "planned",
      {title: "Yeni aile — tanışma", flags: ["unresolved_client"]}),
    // Paid by havale: booked iban_owner, Seda's IBAN.
    cell("e1", "p5", "08:30", "09:20", [client("k14", "Erken Kuş", {
      recorded: {serviceIds: ["s14"], covered: true, paidTL: 4000, uncoveredTL: 0,
        channels: ["iban_owner"], linkedChannels: ["iban_owner"]},
    })], "paid"),
    // A three-child group: the third row must still be reachable.
    cell("e3", "p5", "11:00", "11:50", [
      client("k20", "Bora Tan", {recorded: {serviceIds: ["s20"], covered: false},
        money: {collectNowTL: 1500, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 4000}}),
      client("k21", "Ece Uz", {attendance: {state: "failed", autoCancel: null, confirmMark: null, eligible: true},
        birthday: bday(-5, 5)}),
      client("k22", "Mert Kaya Öztürk", {attendance: {state: "text", autoCancel: null,
        confirmMark: null, eligible: true}, deskNote: "kardeşi de gelecek"}),
    ], "planned", {flags: ["partially_recorded", "unrecorded_past"]}),
    // A free pair (fee 0): the pay sheet records the seans, takes nothing.
    cell("e2", "p5", "20:30", "21:30", [client("k15", "Geç Kuş", {
      money: {collectNowTL: 0, creditTL: 0, restrictedCreditTL: 0, providerCreditTL: 0, feeTL: 0},
    })], "planned"),
  ],
  cancelled: [
    {cancellationId: "x1", kind: "cancelled", providerId: "p1", eventId: "a7",
      time: "12:00", endTime: "12:50", clientIds: ["k16"], clientNames: ["Efe Sarı"],
      reason: "hasta", cancelledBy: "client", restorable: true},
    // A break in Pera's own series (not Selin's, at the same hour).
    {cancellationId: "x2", kind: "paused", providerId: "p3", eventId: "c6",
      time: "15:00", endTime: null, clientIds: ["k17"], clientNames: ["Pera Ün"],
      reason: "tatil", cancelledBy: "client", restorable: false},
  ],
  unplanned: [
    {serviceId: "u1", providerId: "p2", clientId: "k18", name: "Walk-in Aile",
      time: "16:00", recorded: {serviceIds: ["u1"], covered: true, paidTL: 4000,
        uncoveredTL: 0, channels: ["pos"]}},
    {serviceId: "u2", providerId: "p3", clientId: "k19", name: "Saatsiz Kayıt",
      time: null, recorded: {serviceIds: ["u2"], covered: false}},
  ],
  warnings: ["Rayan: takvim okunamadı — sütun boş (503 backend)"],
  totals: {
    sessions: {count: 7, feeTL: 26500, vatTL: 0, unrecordedPast: 4},
    coverage: {
      paidTodayTL: 16000, fromCreditTL: 4000, paidLaterTL: 0, openTL: 5000,
      open: [
        {clientId: "k2", name: "Ege Kara", amountTL: 3500},
        {clientId: "k20", name: "Bora Tan", amountTL: 1500},
      ],
    },
    collections: {
      // Havale booked both ways (bank before 11.10.2026): one figure.
      totalTL: 36500, byChannel: {cash: 20500, iban_owner: 8000, bank: 4000, pos: 4000},
      forEarlierTL: 0, prepaidTL: 12000,
    },
    expenses: {
      totalTL: 10850, fromKasaTL: 850,
      items: [
        {category: "ofis", subcategory: "kirtasiye", amountTL: 850, from: "kasa", note: "toner"},
        {category: "bina", subcategory: null, amountTL: 10000, from: "owner",
          note: "pimapen tamiratı"},
      ],
    },
    // 2.800 from the day before, the morning's cash, Ceyda's hakediş
    // and a toner: 18.850. Held for the others: Merve's 7.500 and her
    // 17:30's 2.400, Gül's 2.000 — Seda can take 6.950.
    kasa: {
      balanceTL: 18850, deltaTL: 16050, toSedaTL: 0, safeDevirTL: 6950, heldTL: 11900,
      devirNotes: ["Rayan: takvim okunamadı — bugün kalan seansları için pay ayrılamadı; " +
        "rakamı elle kontrol edin."],
      count: null,
      moves: [
        {kind: "collection", name: "Ada Yılmaz", time: "09:00", amountTL: 4000, note: null},
        {kind: "collection", name: "Yaman Öz", time: "09:30", amountTL: 4000, note: null},
        {kind: "collection", name: "Nehir Taş", time: "10:00", amountTL: 4000, note: null},
        {kind: "collection", name: "Can Demir", time: "11:00", amountTL: 4000, note: null},
        {kind: "payout", name: "Ceyda", time: null, amountTL: -3600, note: null},
        {kind: "expense", name: "ofis / kirtasiye", time: null, amountTL: -850, note: "toner"},
        {kind: "collection", name: "Kerem Aslan", time: "14:00", amountTL: 4500, note: null},
      ],
    },
    payouts: {
      ownerProviderId: "p1",
      due: [
        {providerId: "p2", name: "Merve", payNowTL: 7500, balanceTL: 7500, inToday: true,
          items: [
            {date: DATE, what: "Yaman Öz seansı", shareTL: 2400},
            {date: DATE, what: "Nehir Taş seansı", shareTL: 2400},
            {date: DATE, what: "Kerem Aslan seansı", shareTL: 2700},
          ],
          unrecorded: {count: 1, estimateTL: 2400}},
        {providerId: "p3", name: "Ceyda", payNowTL: 0, balanceTL: 0, inToday: true,
          items: [], unrecorded: {count: 0, estimateTL: 0}},
        {providerId: "p5", name: "Elif", payNowTL: 0, balanceTL: -1770, inToday: true,
          items: [], unrecorded: {count: 0, estimateTL: 0}},
        {providerId: "p6", name: "Gül", payNowTL: 2000, balanceTL: 2000, inToday: false,
          items: [{date: daysBefore(3), what: "Lale Su seansı", shareTL: 2000}],
          unrecorded: {count: 0, estimateTL: 0}},
      ],
      seda: {owedTL: 9600, safeTL: 6950},
      shortTL: 0,
      paid: [
        {providerId: "p3", name: "Ceyda", amountTL: 3600, from: "kasa", toSeda: false},
      ],
    },
  },
};

export const DEMO_ME: Me = {
  uid: "demo", email: "demo@nefes.local", profile: "admin", providerId: "",
  scopes: ["admin"], money: true,
};

export const DEMO_CLIENT: ClientBasics = {
  clientId: "k7", name: "Lina Koç", status: "active",
  providerId: "p1", providerName: "Seda", sharedProviders: [],
  dateOfBirth: "2019-10-14", sessionFee: 4000,
  contacts: [
    {contactId: "ct1", relation: "anne", name: "Elif Koç", phone: "0532 000 00 01",
      whatsappConsent: "granted"},
    {contactId: "ct2", relation: "baba", name: "Murat Koç", phone: "0533 000 00 02",
      whatsappConsent: null},
  ],
  messagingContactId: "ct1",
  sozlesme: {mark: "missing", text: "sözleşme yok"},
  deskNote: {text: "veli 10 dk geç gelecek", at: `${DATE}T08:00:00.000Z`, by: "bilgi@nefes"},
  address: "Üsküdar", notes: "Servisle geliyor.",
  canEdit: true,
};

// The drawer's answer for a fixture cell or slot — enough of every
// section to work on its shape without the door.
export function demoDetail(key: {providerId: string; eventId: string; date: string}):
  SessionDetail | null {
  const c = DEMO_BOARD.cells.find((x) => x.id === key.eventId);
  const rec = DEMO_BOARD.cancelled.find((r) => r.eventId === key.eventId);
  if (!c && !rec) return null;
  const providerName = DEMO_BOARD.providers
    .find((p) => p.providerId === key.providerId)?.name ?? key.providerId;
  // Efe's family answered "Katılamayacağız" before the desk cancelled:
  // a slot keeps its WhatsApp row.
  const kids: BoardClient[] = c ? c.clients :
    (rec as NonNullable<typeof rec>).clientIds.map((id, i) =>
      client(id, (rec as NonNullable<typeof rec>).clientNames[i] ?? id, id === "k16" ?
        {attendance: {state: "declined", autoCancel: null, confirmMark: null, eligible: true}} :
        {}));
  const time = c?.time ?? rec?.time ?? "10:00";
  const stamp = (t: string) => `${DATE}T${t}:00.000Z`;

  const whatsapp = (k: BoardClient): WaRowView | null => {
    const s = k.attendance.state;
    if (s === "none") return null;
    if (k.attendance.source === "phone") {
      return {state: s, delivery: "phone", error: null,
        response: {kind: "confirmed", text: "Telefonda onaylandı", at: stamp("07:15"),
          source: "phone", by: "bilgi@nefesdilkonusma.com"},
        autoCancel: null, confirmMark: k.attendance.confirmMark, contactName: "",
        sessionDate: DATE, sessionTime: time, createdAt: stamp("07:15"),
        updatedAt: stamp("07:15")};
    }
    const replied = s === "confirmed" || s === "declined" || s === "text";
    return {
      state: s,
      delivery: replied ? "read" : s === "failed" ? "failed" : s,
      error: s === "failed" ? "(#131026) Message undeliverable" : null,
      response: replied ? {
        kind: s,
        text: s === "text" ? "Bugün biraz geç kalabiliriz, 10 dk" :
          s === "confirmed" ? "Geleceğiz" : "Katılamayacağız",
        at: stamp("06:40"),
      } : null,
      autoCancel: k.attendance.autoCancel, confirmMark: k.attendance.confirmMark,
      contactName: "Elif Koç", sessionDate: DATE, sessionTime: time,
      createdAt: `${daysBefore(1)}T11:00:00.000Z`, updatedAt: stamp("06:40"),
    };
  };
  // "Son seanslar": the weeks before, newest first — paid, owed, an
  // iptal, one with another danışman — what is open adding up to the
  // board's collectNow, and the seans itself on top once it is recorded
  // (or cancelled). One child has no list (a door that predates it),
  // one a list with nothing in it yet (a family new this week).
  const other = DEMO_BOARD.providers.find((x) => x.providerId !== key.providerId) ??
    DEMO_BOARD.providers[0];
  const history = (k: BoardClient): SessionHistoryRow[] | undefined => {
    if (k.clientId === "k21") return undefined;
    if (k.clientId === "k15") return [];
    const fee = k.money?.feeTL ?? 4000;
    const row = (date: string, o: Partial<SessionHistoryRow> = {}): SessionHistoryRow => ({
      date, time, providerId: key.providerId, providerName, status: "done", feeTL: fee,
      remainingTL: 0, kind: "regular", reason: null, cancelledBy: null,
      serviceId: `${k.clientId}-${date}`, cancellationId: null, ...o,
    });
    const iptal = (date: string, reason: string, cancelledBy: string) => row(date, {
      status: "cancelled", feeTL: null, remainingTL: null, kind: null, reason, cancelledBy,
      serviceId: null, cancellationId: `${k.clientId}-x-${date}`});
    let open = k.money?.collectNowTL ?? 0;
    const owe = () => {
      const left = Math.max(0, Math.min(fee, open));
      open -= left;
      return left;
    };
    const rows: SessionHistoryRow[] = [];
    if (k.recorded) {
      const left = k.recorded.uncoveredTL ?? (k.recorded.covered ? 0 : fee);
      open -= left;
      rows.push(row(DATE, {remainingTL: left}));
    }
    if (rec && rec.kind === "cancelled") rows.push(iptal(DATE, rec.reason, rec.cancelledBy));
    rows.push(
      row(daysBefore(7)),
      row(daysBefore(14), {remainingTL: owe()}),
      iptal(daysBefore(21), "hasta", "client"),
      row(daysBefore(28), {remainingTL: owe()}),
      row(daysBefore(35), {providerId: other.providerId, providerName: other.name,
        kind: "evaluation", remainingTL: owe()}),
      row(daysBefore(42), {kind: "makeup"}),
      row(daysBefore(49)),
    );
    return rows;
  };
  const money = (k: BoardClient, hist: SessionHistoryRow[] | undefined): ClientMoneyView | null => {
    const m = k.money;
    if (!m) return null;
    const owed = m.collectNowTL > 0;
    const open = (hist ?? []).filter((r) => (r.remainingTL ?? 0) > 0);
    return {
      feeTL: m.feeTL, collectNowTL: m.collectNowTL, creditTL: m.creditTL,
      restrictedCreditTL: m.restrictedCreditTL,
      restrictedCredit: m.providerCreditTL > 0 ?
        [{providerId: key.providerId, providerName, amountTL: m.providerCreditTL}] : [],
      balanceTL: m.creditTL - m.collectNowTL,
      explanation: owed ? `Alınacak: ${m.collectNowTL.toLocaleString("tr-TR")},00 TL ` +
        `(${(open.length ? open.map((r) => r.date) : [daysBefore(14)])
          .map((d) => `${d.slice(8, 10)}.${d.slice(5, 7)}`).join(", ")} seansı).` :
        m.creditTL > 0 ? `Ön ödemesi var: ${m.creditTL.toLocaleString("tr-TR")},00 TL kredi — tahsilat gerekmez.` :
          "Hesap kapalı — borç da kredi de yok.",
      openServices: !owed ? [] : open.length ? open.map((r) => ({
        serviceId: r.serviceId ?? r.date, date: r.date, providerId: r.providerId,
        providerName: r.providerName, feeTL: r.feeTL ?? 0, remainingTL: r.remainingTL ?? 0,
        kind: r.kind ?? "regular", carriedFromOldSystem: false})) :
        [{serviceId: `${k.clientId}-open`, date: daysBefore(14),
          providerId: key.providerId, providerName, feeTL: m.feeTL ?? 4000,
          remainingTL: m.collectNowTL, kind: "regular", carriedFromOldSystem: false}],
      payments: [
        {paymentId: `${k.clientId}-p1`, date: daysBefore(7), amountTL: 4000, channel: "cash",
          purpose: "session_income", providerId: null, providerName: null, note: null,
          serviceDates: [daysBefore(7)]},
        {paymentId: `${k.clientId}-p2`, date: daysBefore(42), amountTL: 4000, channel: "pos",
          purpose: "session_income", providerId: null, providerName: null, note: null,
          serviceDates: [daysBefore(42)]},
        {paymentId: `${k.clientId}-p3`, date: daysBefore(50), amountTL: 12000, channel: "bank",
          purpose: "session_income", providerId: null, providerName: null,
          note: "3 seans ön ödeme", serviceDates: []},
      ],
      warnings: [],
    };
  };
  const clients: SessionClientDetail[] = kids.map((k) => {
    const hist = history(k);
    const m = money(k, hist);
    return {
      clientId: k.clientId, name: k.name,
      basics: {
        ...DEMO_CLIENT, clientId: k.clientId, name: k.name,
        providerId: key.providerId, providerName, sozlesme: k.sozlesme,
        sessionFee: k.money?.feeTL ?? null,
        deskNote: k.deskNote ?
          {text: k.deskNote, at: stamp("05:00"), by: "bilgi@nefes"} : null,
      },
      notes: [
        {noteId: `${k.clientId}-n1`, date: daysBefore(7), category: "session",
          text: "Okulda /s/ sesini cümle içinde kullanmaya başladı. Evde kart oyunu devam.",
          author: providerName, createdAt: `${daysBefore(7)}T09:00:00.000Z`},
        {noteId: `${k.clientId}-n2`, date: daysBefore(14), category: "session",
          text: "Artikülasyon /r/ çalışıldı; ev ödevi verildi.", author: providerName,
          createdAt: `${daysBefore(14)}T09:00:00.000Z`},
        {noteId: `${k.clientId}-n3`, date: daysBefore(21), category: "family",
          text: "Anne okuldaki durumu anlattı — öğretmenle görüşülecek.", author: providerName,
          createdAt: `${daysBefore(21)}T09:00:00.000Z`},
        ...(k.deskNote ? [{noteId: `${k.clientId}-n0`, date: DATE, category: "other",
          text: k.deskNote, author: "", createdAt: stamp("05:00")}] : []),
        ...(rec?.kind === "paused" ? [{noteId: `${k.clientId}-n4`, date: daysBefore(1),
          category: "family", text: "Aile iki hafta tatilde — 28.10'da dönüyorlar.",
          author: "", createdAt: `${daysBefore(1)}T10:00:00.000Z`}] : []),
      ].sort((a, b) => b.date.localeCompare(a.date)),
      whatsapp: whatsapp(k),
      ...(m ? {money: m} : {}),
      ...(hist ? {history: hist} : {}),
    };
  });
  return {
    providerId: key.providerId, providerName, date: DATE,
    event: c ? {
      eventId: c.id, summary: `${c.title}${c.status === "confirmed" ? " 📅✔️" : ""}`,
      title: c.title,
      description: c.scheduleId ? `[Nefes session: ${c.scheduleId}]\nServisle geliyor` : null,
      status: "confirmed", live: true, date: DATE, time: c.time, endTime: c.endTime,
      durationMin: c.durationMin, scheduleId: c.scheduleId,
      recurringEventId: c.recurringEventId, updated: `${daysBefore(2)}T14:12:00.000Z`,
    } : {
      eventId: key.eventId, summary: `${kids.map((k) => k.name).join(", ")} — ${providerName}`,
      title: `${kids.map((k) => k.name).join(", ")} — ${providerName}`, description: null,
      status: "cancelled", live: false, date: DATE, time, endTime: rec?.endTime ?? null,
      durationMin: rec?.endTime ? 50 : null, scheduleId: null, recurringEventId: "m-x",
      updated: null,
    },
    schedule: c?.recurringEventId ? {
      // DATE's weekday; Tuna's seans was moved an hour for today, so his
      // drawer reads "Bugün 17:00", not the series.
      scheduleId: c.scheduleId ?? "s", providerId: key.providerId,
      dayOfWeek: new Date(`${DATE}T12:00:00Z`).getUTCDay() || 7,
      time: c.id === "c5" ? "16:00" : c.time,
      durationMin: c.durationMin, recurrence: c.id === "a3" ? "biweekly" : "weekly",
      format: c.isGroup ? "group" : "individual", room: c.isGroup ? "large" : "small",
      clientIds: kids.map((k) => k.clientId), clientNames: kids.map((k) => k.name),
      notes: "", linked: c.id !== "b4",
    } : null,
    cancellation: rec ? {
      cancellationId: rec.cancellationId, kind: rec.kind, reason: rec.reason,
      cancelledBy: rec.cancelledBy, source: "http", recordedBy: "bilgi@nefes",
      createdAt: stamp("06:20"), restoredAt: null, restoredBy: null,
      restorable: rec.restorable, standing: true,
    } : c?.id === "a2" ? {
      cancellationId: "x-old", kind: "cancelled", reason: "hasta", cancelledBy: "client",
      source: "mcp", recordedBy: "bilgi@nefes", createdAt: `${daysBefore(1)}T08:00:00.000Z`,
      restoredAt: `${daysBefore(1)}T08:05:00.000Z`, restoredBy: "onur@nefes",
      restorable: true, standing: false,
    } : null,
    clients,
    warnings: c?.id === "b4" ? [`Bu seans artık ${daysBefore(-1)} gününde — pano eski olabilir.`] : [],
  };
}

// Seans ekle's search list on the demo day: a few cards, one pasif,
// two that share a first name, one of Seda's.
export const DEMO_DESK: DeskClients = {
  ownerProviderId: "p1",
  clients: [
    {clientId: "k1", name: "Ada Yılmaz", status: "active", providerId: "p1",
      providerName: "Seda", terms: {p1: {feeTL: 4000, rate: 0}}},
    {clientId: "k2", name: "Ege Kara", status: "active", providerId: "p2",
      providerName: "Merve", terms: {p2: {feeTL: 4000, rate: 50}}},
    {clientId: "k3", name: "Deniz Ak", status: "active", providerId: "p1",
      providerName: "Seda", terms: {p1: {feeTL: 4500, rate: 0}}},
    {clientId: "k4", name: "Deniz Akın", status: "inactive", providerId: "p3",
      providerName: "Ceyda", terms: {p3: {feeTL: 3500, rate: 40}}},
    {clientId: "k5", name: "Lina Koç", status: "active", providerId: "p1",
      providerName: "Seda", terms: {p1: {feeTL: 4000, rate: 0}}},
    {clientId: "k6", name: "Yaman Öz", status: "active", providerId: "p2",
      providerName: "Merve", terms: {p2: {feeTL: null, rate: 50}}},
  ],
};
